// Fulltext search pagination rendering

import { getPaginationElement, clearPagination, getTranslation, escapeHtml } from './helpers.js';
import { performFulltextSearch } from './search-api.js';

// Lock to prevent multiple simultaneous renders
let isRenderingPagination = false;
let paginationRenderCount = 0;

/**
 * Render pagination for fulltext search results
 * @param {Object} pagination - Pagination data
 * @param {string} query - Search query
 * @param {number} totalResults - Total results count (optional, for refine message)
 */
export function renderFulltextPagination(pagination, query, totalResults = null) {
    paginationRenderCount++;
    const renderId = paginationRenderCount;
    
    // Prevent concurrent calls
    if (isRenderingPagination) {
        return;
    }
    
    try {
        isRenderingPagination = true;
        
        const $container = $('#nav-full-text');
        // Find .pages element inside #nav-full-text tab (should already exist, created in render-results.js)
        let pagesElement = $container.find('.pages').first()[0];
        if (!pagesElement) {
            // Fallback: create pagination container if it doesn't exist (shouldn't happen normally)
            const $pagesContainer = $('<nav class="pages" aria-label="Page navigation"></nav>');
            // Insert after #fulltext-results-container
            const $resultsContainer = $container.find('#fulltext-results-container');
            if ($resultsContainer.length) {
                $pagesContainer.insertAfter($resultsContainer);
            } else {
                // Fallback: append to container
                $container.append($pagesContainer);
            }
            pagesElement = $pagesContainer[0];
        }
        if (!pagesElement) {
            isRenderingPagination = false;
            return;
        }
        // Clear pagination before rendering new one (replacement, not accumulation)
        // Clear both inside tab and global (for backward compatibility)
        pagesElement.innerHTML = '';
        const globalPagesElement = getPaginationElement();
        if (globalPagesElement && globalPagesElement !== pagesElement) {
            globalPagesElement.innerHTML = '';
        }
        
        if (!pagination || !pagination.total_pages || pagination.total_pages <= 1) {
            // Remove top pagination if no pages
            const $req = $container.find('.search-request-text');
            $req.find('.pages-top').remove();
            // Clear pagination completely
            pagesElement.innerHTML = '';
            isRenderingPagination = false;
            return;
        }
        
        const current = Number(pagination.current_page) || 1;
        const totalRaw = Number(pagination.total_pages) || 1;
        // Limit pagination to maximum 30 pages
        const total = Math.min(totalRaw, 30);
        
        // Build pagination HTML as string first
        let paginationHTML = '<ul class="pagination justify-content-center my-3">';
        
        // First page button (В начало) - show only if not on first page
        const isFirstPage = current <= 1;
        if (!isFirstPage) {
            paginationHTML += `<li class="page-item">`;
            paginationHTML += `<a class="page-link" href="#" data-page="1" title="${getTranslation('pagination_first', 'First page')}"><i class="bi bi-chevron-double-left"></i></a>`;
            paginationHTML += '</li>';
        }
        
        // Previous button
        const prevDisabled = current <= 1;
        const prevPage = Math.max(1, current - 1);
        paginationHTML += `<li class="page-item${prevDisabled ? ' disabled' : ''}">`;
        if (!prevDisabled) {
            paginationHTML += `<a class="page-link" href="#" data-page="${prevPage}" title="${getTranslation('pagination_prev', 'Previous page')}"><i class="bi bi-chevron-left"></i></a>`;
        } else {
            paginationHTML += `<a class="page-link" href="#" title="${getTranslation('pagination_prev', 'Previous page')}"><i class="bi bi-chevron-left"></i></a>`;
        }
        paginationHTML += '</li>';
        
        // Page numbers
        const start = Math.max(1, current - 2);
        const end = Math.min(total, current + 2);
        for (let p = start; p <= end; p++) {
            const isActive = p === current;
            paginationHTML += `<li class="page-item${isActive ? ' active' : ''}">`;
            paginationHTML += `<a class="page-link" href="#" data-page="${p}">${p}</a>`;
            paginationHTML += '</li>';
        }
        
        // Next button
        const nextDisabled = current >= total;
        const nextPage = Math.min(total, current + 1);
        paginationHTML += `<li class="page-item${nextDisabled ? ' disabled' : ''}">`;
        if (!nextDisabled) {
            paginationHTML += `<a class="page-link" href="#" data-page="${nextPage}" title="${getTranslation('pagination_next', 'Next page')}"><i class="bi bi-chevron-right"></i></a>`;
        } else {
            paginationHTML += `<a class="page-link" href="#" title="${getTranslation('pagination_next', 'Next page')}"><i class="bi bi-chevron-right"></i></a>`;
        }
        paginationHTML += '</li>';
        
        // Last page button (В конец) - show only if not on last page
        const isLastPage = current >= total;
        if (!isLastPage) {
            paginationHTML += `<li class="page-item">`;
            paginationHTML += `<a class="page-link" href="#" data-page="${total}" title="${getTranslation('pagination_last', 'Last page')}"><i class="bi bi-chevron-double-right"></i></a>`;
            paginationHTML += '</li>';
        }
        
        paginationHTML += '</ul>';
        
        // Show refine query message if on page 30 and total results >= 300
        const shouldShowRefineMessage = current === 30 && totalResults !== null && totalResults >= 300;
        if (shouldShowRefineMessage) {
            const refineMessage = getTranslation('pagination_refine_query', 'Refine your search criteria and try again.');
            paginationHTML += `<div class="text-center text-muted mt-3 mb-2" style="font-size: 0.9rem;">${escapeHtml(refineMessage)}</div>`;
        }
        
        // ATOMIC REPLACEMENT - replace entire innerHTML at once
        pagesElement.innerHTML = paginationHTML;
        
        // Attach event listeners after DOM is updated
        // Remove old listeners first to prevent duplicates
        const oldLinks = pagesElement.querySelectorAll('a.page-link[data-page]');
        oldLinks.forEach(link => {
            const newLink = link.cloneNode(true);
            link.parentNode.replaceChild(newLink, link);
        });
        
        const links = pagesElement.querySelectorAll('a.page-link[data-page]');
        
        // Track if click handler is already attached
        let isClickHandled = false;
        const clickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Prevent multiple rapid clicks
            if (isClickHandled) {
                return;
            }
            
            // Get page from clicked element or its parent (in case icon was clicked)
            let pageElement = e.target;
            let pageAttr = pageElement.getAttribute('data-page');
            
            // If clicked on icon, find parent link with data-page
            if (!pageAttr && pageElement.tagName !== 'A') {
                pageElement = pageElement.closest('a.page-link[data-page]');
                if (pageElement) {
                    pageAttr = pageElement.getAttribute('data-page');
                }
            }
            
            // Use currentTarget as fallback (the link element itself)
            if (!pageAttr && e.currentTarget) {
                pageAttr = e.currentTarget.getAttribute('data-page');
                pageElement = e.currentTarget;
            }
            
            const page = parseInt(pageAttr, 10);
            if (isNaN(page) || page < 1) return;
            
            isClickHandled = true;
            
            // Add loading state to clicked button
            const $clickedLink = $(pageElement || e.currentTarget);
            $clickedLink.addClass('page-link--loading');
            
            // Update URL with page parameter before performing search
            try {
                const url = new URL(window.location);
                url.searchParams.set('page', String(page));
                // Remove page=1 to keep URL clean
                if (page === 1) {
                    url.searchParams.delete('page');
                }
                window.history.pushState({}, '', url);
            } catch(_) {}
            try { 
                performFulltextSearch(query, page).then(() => {
                    // Remove loading state after search completes
                    $clickedLink.removeClass('page-link--loading');
                    
                    // Smooth scroll to top of #nav-tabContent after pagination (only upward)
                    setTimeout(() => {
                        const tabContentEl = document.getElementById('nav-tabContent');
                        if (tabContentEl) {
                            // Scroll only upward: check if we're below the container
                            const rect = tabContentEl.getBoundingClientRect();
                            const currentScrollY = window.scrollY || window.pageYOffset || 0;
                            const containerTop = rect.top + currentScrollY;
                            
                            // Scroll only if current position is below container (scroll upward)
                            if (currentScrollY > containerTop) {
                                tabContentEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }
                    }, 150);
                }).catch((err) => {
                    // Remove loading state on error
                    $clickedLink.removeClass('page-link--loading');
                });
            } catch(err) {
                // Remove loading state on error
                $clickedLink.removeClass('page-link--loading');
            } finally {
                // Reset flag after delay
                setTimeout(() => { isClickHandled = false; }, 1000);
            }
        };
        
        links.forEach(link => {
            link.addEventListener('click', clickHandler, { once: false });
        });
        
        // Remove top pagination if it exists (no longer needed)
        const $req = $container.find('.search-request-text');
        if ($req.length) {
            $req.find('.pages-top').remove();
        }
        
        // MutationObserver to track DOM changes
        const observer = new MutationObserver(() => {
            // Observer for tracking DOM changes (silent)
        });
        
        observer.observe(pagesElement, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false
        });
        
        // Stop observing after 2 seconds
        setTimeout(() => {
            observer.disconnect();
        }, 2000);
        
    } catch(e) {
        // Error handling without debug logs
    } finally {
        // Always release lock
        isRenderingPagination = false;
    }
}

